'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Globe, Menu, X, ChevronDown } from 'lucide-react';

const languages = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'hi', label: 'हिंदी', flag: '🇮🇳' },
];

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [activeLang, setActiveLang] = useState(languages?.[0]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/landing-page" className="flex items-center gap-2.5">
          <AppLogo size={34} />
          <span className="font-bold text-lg text-gray-900 tracking-tight">VisaPilot</span>
          <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100">AI</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {[
            { label: 'How it works', href: '#how-it-works' },
            { label: 'Features', href: '#features' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'For Agencies', href: '#pricing' },
          ]?.map((item) => (
            <a
              key={`nav-${item?.label?.toLowerCase()?.replace(/\s/g, '-')}`}
              href={item?.href}
              className="px-3.5 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-150"
            >
              {item?.label}
            </a>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-2">
          {/* Language Picker */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-150"
            >
              <Globe size={15} />
              <span>{activeLang?.flag} {activeLang?.code?.toUpperCase()}</span>
              <ChevronDown size={13} className={`transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-xl border border-gray-100 shadow-lg py-1 animate-fade-in">
                {languages?.map((lang) => (
                  <button
                    key={`lang-${lang?.code}`}
                    onClick={() => { setActiveLang(lang); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors duration-100 ${
                      activeLang?.code === lang?.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{lang?.flag}</span>
                    <span>{lang?.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            href="/sign-up-login-screen"
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-150"
          >
            Sign in
          </Link>
          <Link
            href="/dashboard"
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-150"
          >
            Dashboard
          </Link>
          <Link
            href="/agency-portal"
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-150"
          >
            Agency Portal
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="btn-primary text-sm py-2 px-4"
          >
            Start free →
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1 animate-fade-in">
          {['How it works', 'Features', 'Pricing', 'For Agencies']?.map((item) => (
            <a
              key={`mobile-nav-${item?.toLowerCase()?.replace(/\s/g, '-')}`}
              href="#"
              className="block px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
              onClick={() => setMobileOpen(false)}
            >
              {item}
            </a>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            <Link href="/sign-up-login-screen" className="btn-secondary text-sm justify-center">Sign in</Link>
            <Link href="/dashboard" className="btn-secondary text-sm justify-center">Dashboard</Link>
            <Link href="/agency-portal" className="btn-secondary text-sm justify-center">Agency Portal</Link>
            <Link href="/sign-up-login-screen" className="btn-primary text-sm justify-center">Start free →</Link>
          </div>
        </div>
      )}
    </header>
  );
}